'use client';
import { startTransition, useContext, useEffect, useRef } from 'react';
import { IoInformationCircleOutline } from 'react-icons/io5';
import { toast } from 'react-hot-toast';
import { usePathname, useSearchParams } from 'next/navigation';

import { Modal, Tooltip } from '@benzinga/core-ui';
import { SessionContext } from '@benzinga/session-context';
import { useWatchlistContext } from '@benzinga/user-context';
import { Watchlist, WatchlistManager, WatchlistSymbol } from '@benzinga/watchlist-manager';

import NotificationTable from './NotificationTable';
import NotificationsOverview from './NotificationsOverview';
import { PortfolioModalHeader } from '../PortfolioModalHeader';
import {
  portfolioModalBodyClass,
  portfolioModalContainerStyle,
  portfolioModalShellClass,
} from '../modalStyles';

interface ToggleProps {
  enabled: boolean;
  onChange: (enabled: boolean) => void;
  disabled?: boolean;
}

export const Toggler: React.FC<ToggleProps> = ({ enabled, onChange, disabled = false }) => {
  return (
    <button
      type="button"
      aria-pressed={enabled}
      onClick={() => !disabled && onChange(!enabled)}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-bzblue-500 focus-visible:ring-offset-2 ${
        enabled ? 'bg-bzblue-700' : 'bg-slate-300'
      } ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
      disabled={disabled}
    >
      <span
        className={`inline-block size-5 transform rounded-full bg-white shadow-sm transition-transform ${
          enabled ? 'translate-x-[22px]' : 'translate-x-0.5'
        }`}
      />
    </button>
  );
};

export const sortRows = (rows: WatchlistSymbol[] | Watchlist[], sortType: string, sortDir: string) => {
  return [...rows].sort((a, b) => {
    const aValue = a[sortType as keyof WatchlistSymbol] || a[sortType as keyof Watchlist];
    const bValue = b[sortType as keyof WatchlistSymbol] || b[sortType as keyof Watchlist];

    if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
      if (aValue === bValue) return 0;
      return sortDir === 'asc' ? (aValue ? 1 : -1) : aValue ? -1 : 1;
    }

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      const comparison = aValue.localeCompare(bValue);
      return sortDir === 'asc' ? comparison : -comparison;
    }

    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortDir === 'asc' ? aValue - bValue : bValue - aValue;
    }

    const aStr = String(aValue || '');
    const bStr = String(bValue || '');
    const comparison = aStr.localeCompare(bStr);
    return sortDir === 'asc' ? comparison : -comparison;
  });
};

export const Notifications = () => {
  const { showNotificationsModal, setShowNotificationsModal, watchlists, setDeleteModal } = useWatchlistContext();
  const session = useContext(SessionContext);
  const watchlistManager = session.getManager(WatchlistManager);
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const action = searchParams?.get('action');
  const hasProcessedAction = useRef(false);

  const openWatchlistNotifications = (watchlist: Watchlist | null) => {
    setShowNotificationsModal({ show: true, watchlist });
  };

  const openAllNotifications = () => {
    setShowNotificationsModal({ show: true, watchlist: null });
  };

  const closeModal = () => {
    setShowNotificationsModal({ show: false, watchlist: null });
  };

  const toggleWatchlistAlert = async (watchlist: Watchlist, val: boolean) => {
    await watchlistManager.updateWatchlistSettings({ ...watchlist, emailSummary: val });
  };

  const updateWatchlistTickerAlert = async (symbol: WatchlistSymbol) => {
    const result = await watchlistManager.updateWatchlistTicker(symbol);
    if (result.err) {
      console.error('Error updating watchlist ticker alert:', result.err);
      toast.error('Error updating watchlist ticker alert. Please try again.');
    } else {
      toast.success('Watchlist ticker alert updated');
    }
  };

  const deleteWatchlistTicker = async (watchlist: Watchlist, ticker: WatchlistSymbol) => {
    const result = await watchlistManager.removeTickersFromWatchlist(watchlist, [ticker]);
    if (result.err) {
      console.error('Error deleting watchlist ticker:', result.err);
      toast.error('Error deleting watchlist ticker. Please try again.');
    } else {
      toast.success('Watchlist ticker deleted');
    }
  };

  const deleteWatchlist = async (watchlist: Watchlist) => {
    setDeleteModal({ show: true, watchlist, isLoading: false });
  };

  useEffect(() => {
    if (action === 'notifications' && !showNotificationsModal.show && !hasProcessedAction.current) {
      if (searchParams) {
        hasProcessedAction.current = true;
        const params = new URLSearchParams(searchParams.toString());
        params.delete('action');
        const newSearch = params.toString();
        const newUrl = `${pathname}${newSearch ? `?${newSearch}` : ''}`;
        window.history.replaceState({}, '', newUrl);

        startTransition(() => {
          setShowNotificationsModal({ show: true, watchlist: null });
        });
      }
    }
  }, [action, searchParams, pathname, setShowNotificationsModal, showNotificationsModal.show]);

  const activeWatchlist = showNotificationsModal.watchlist;

  return (
    <Modal
      visible={showNotificationsModal.show}
      onClose={closeModal}
      doNotCloseOnBackgroundClick={true}
      disableFullScreenButton={true}
      hideTitle={true}
      fullScreen={true}
      size={{ manual: { height: 'auto', width: '720px', maxWidth: 'calc(100vw - 32px)' } }}
      containerStyle={portfolioModalContainerStyle}
    >
      <div className={portfolioModalShellClass}>
        {activeWatchlist ? (
          <PortfolioModalHeader
            title={activeWatchlist.name}
            description="Choose which alerts to send for each ticker"
            onClose={closeModal}
            back={{ label: 'All watchlists', onClick: openAllNotifications }}
          />
        ) : (
          <PortfolioModalHeader
            title="Alerts"
            description="Manage email alerts for each of your watchlists"
            onClose={closeModal}
          />
        )}

        {activeWatchlist ? (
          <div className={portfolioModalBodyClass}>
            <div className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3.5">
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-sm font-semibold text-slate-800">Personalized daily briefing</span>
                  <Tooltip
                    content="Twice a day receive a digest of everything you need to know about your stocks"
                    size="sm"
                  >
                    <span className="text-slate-400 hover:text-bzblue-700">
                      <IoInformationCircleOutline size={16} />
                    </span>
                  </Tooltip>
                </div>
                <p className="mt-0.5 text-xs text-slate-500">A twice-daily digest for this watchlist</p>
              </div>
              <Toggler
                enabled={activeWatchlist.emailSummary}
                onChange={val => toggleWatchlistAlert(activeWatchlist, val)}
              />
            </div>
            <NotificationTable
              watchlist={activeWatchlist}
              onRemoveNotificationTableRow={deleteWatchlistTicker}
              onUpdateWatchlistTicker={updateWatchlistTickerAlert}
            />
          </div>
        ) : (
          <div className={portfolioModalBodyClass}>
            <NotificationsOverview
              rows={watchlists}
              onSelectWatchlist={openWatchlistNotifications}
              onShowRemoveWatchlist={deleteWatchlist}
              onUpdateWatchlistAlert={toggleWatchlistAlert}
            />
          </div>
        )}
      </div>
    </Modal>
  );
};
